/**
 * Seção Imóveis do Relatório
 * KPIs + Tabela + Gráfico de barras empilhadas por tipo de unidade
 */
import { useMemo, useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { API_BASE_URL } from '@/lib/api';
import { Building2, CheckCircle, Clock, Download, Loader2 } from 'lucide-react';
import { useImoveisData, useRelatoriosFilters, type ImoveisResponse, type ImoveisScope, type ImoveisTypeLine } from '../hooks';
import { buildImoveisExportWorkbook, calculateImoveisPercent } from '../services/imoveisExcelExport';

const IMOVEIS_SCOPE_OPTIONS: Array<{ value: ImoveisScope; label: string }> = [
  { value: 'todos', label: 'Todos imóveis' },
  { value: 'avcb', label: 'AVCB' },
  { value: 'alvara', label: 'Alvará de Funcionamento' },
];

const scopeLabels: Record<ImoveisScope, { subtitle: string; validosTitle: string; mappedTitle: string; regular: string; andamento: string; irregular: string }> = {
  todos: {
    subtitle: 'Classificação de unidades por status de documentos AVCB e Alvará',
    validosTitle: '% Válidas',
    mappedTitle: '% Ambos Docs',
    regular: 'AVCB e Alvará ambos em status regular (Vigente, Permanente ou Isento)',
    andamento: 'Pelo menos um documento em obtenção ou renovação',
    irregular: 'Demais casos (vencido, inexistente, não configurado)',
  },
  avcb: {
    subtitle: 'Classificação de unidades por status de documentos AVCB',
    validosTitle: '% Regulares',
    mappedTitle: '% Com AVCB',
    regular: 'AVCB em status regular (Vigente, Permanente ou Isento)',
    andamento: 'AVCB em obtenção ou renovação',
    irregular: 'AVCB vencido, inexistente ou sem documento ativo',
  },
  alvara: {
    subtitle: 'Classificação de unidades por status de Alvará de Funcionamento',
    validosTitle: '% Regulares',
    mappedTitle: '% Com Alvará',
    regular: 'Alvará em status regular (Vigente, Permanente ou Isento)',
    andamento: 'Alvará em obtenção ou renovação',
    irregular: 'Alvará vencido, inexistente ou sem documento ativo',
  },
};

// ============================================================================
// KPI CARD
// ============================================================================

interface KpiCardProps {
  title: string;
  value: number;
  percent?: number;
  icon: React.ReactNode;
  color: 'default' | 'green' | 'blue' | 'amber' | 'red';
  isLoading?: boolean;
}

function KpiCard({ title, value, percent, icon, color, isLoading }: KpiCardProps) {
  const colorClasses = {
    default: '',
    green: 'border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20',
    blue: 'border-blue-500/30 bg-blue-50/50 dark:bg-blue-950/20',
    amber: 'border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20',
    red: 'border-red-500/30 bg-red-50/50 dark:bg-red-950/20',
  };

  const textColors = {
    default: 'text-foreground',
    green: 'text-emerald-600 dark:text-emerald-400',
    blue: 'text-blue-600 dark:text-blue-400',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
  };

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg border p-4 animate-pulse">
        <div className="h-4 bg-muted rounded w-24 mb-2" />
        <div className="h-8 bg-muted rounded w-16" />
      </div>
    );
  }

  return (
    <div className={cn('bg-card rounded-lg border p-4', colorClasses[color])} data-pdf-block>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
        <span className={textColors[color]}>{icon}</span>
      </div>
      <div className={cn('text-3xl font-bold', textColors[color])}>
        {percent !== undefined ? `${percent.toFixed(1)}%` : value.toLocaleString('pt-BR')}
      </div>
      {percent !== undefined && (
        <div className="text-xs text-muted-foreground mt-1">
          {value.toLocaleString('pt-BR')} unidades
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TABELA DE TIPOS
// ============================================================================

interface TiposTableProps {
  data: ImoveisTypeLine[];
  viewMode: 'absoluto' | 'percentual';
  isLoading?: boolean;
}

function TiposTable({ data, viewMode, isLoading }: TiposTableProps) {
  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-muted rounded mb-2" />
        <div className="h-8 bg-muted rounded mb-2" />
        <div className="h-8 bg-muted rounded mb-2" />
        <div className="h-8 bg-muted rounded" />
      </div>
    );
  }

  const formatValue = (value: number, total: number) => {
    if (viewMode === 'percentual') {
      return total > 0 ? `${calculateImoveisPercent(value, total).toFixed(1)}%` : '0%';
    }
    return value.toLocaleString('pt-BR');
  };

  // Totais
  const totals = data.reduce(
    (acc, row) => ({
      regular: acc.regular + row.regular,
      andamento: acc.andamento + row.andamento,
      irregular: acc.irregular + row.irregular,
      total: acc.total + row.total,
    }),
    { regular: 0, andamento: 0, irregular: 0, total: 0 }
  );

  return (
    <div className="overflow-x-auto" data-pdf-block>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="text-left py-2 px-3 font-medium">Tipo</th>
            <th className="text-right py-2 px-3 font-medium text-emerald-600 dark:text-emerald-400">Regular</th>
            <th className="text-right py-2 px-3 font-medium text-amber-600 dark:text-amber-400">Andamento</th>
            <th className="text-right py-2 px-3 font-medium text-red-600 dark:text-red-400">Irregular</th>
            <th className="text-right py-2 px-3 font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={row.tipo} className={cn('border-b', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
              <td className="py-2 px-3 font-medium">{row.tipo}</td>
              <td className="py-2 px-3 text-right text-emerald-600 dark:text-emerald-400">
                {formatValue(row.regular, row.total)}
              </td>
              <td className="py-2 px-3 text-right text-amber-600 dark:text-amber-400">
                {formatValue(row.andamento, row.total)}
              </td>
              <td className="py-2 px-3 text-right text-red-600 dark:text-red-400">
                {formatValue(row.irregular, row.total)}
              </td>
              <td className="py-2 px-3 text-right font-medium">{row.total.toLocaleString('pt-BR')}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 bg-muted/50 font-bold">
            <td className="py-2 px-3">TOTAL</td>
            <td className="py-2 px-3 text-right text-emerald-600 dark:text-emerald-400">
              {formatValue(totals.regular, totals.total)}
            </td>
            <td className="py-2 px-3 text-right text-amber-600 dark:text-amber-400">
              {formatValue(totals.andamento, totals.total)}
            </td>
            <td className="py-2 px-3 text-right text-red-600 dark:text-red-400">
              {formatValue(totals.irregular, totals.total)}
            </td>
            <td className="py-2 px-3 text-right">{totals.total.toLocaleString('pt-BR')}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ============================================================================
// GRÁFICO DE BARRAS EMPILHADAS
// ============================================================================

interface StackedChartProps {
  data: ImoveisTypeLine[];
  viewMode: 'absoluto' | 'percentual';
  isLoading?: boolean;
}

// Hook para detectar tema dark
function useIsDarkMode() {
  const [isDark, setIsDark] = useState(() => 
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    
    return () => observer.disconnect();
  }, []);

  return isDark;
}

function StackedChart({ data, viewMode, isLoading }: StackedChartProps) {
  const isDark = useIsDarkMode();
  const textColor = isDark ? '#e5e7eb' : '#374151';

  const option: EChartsOption = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Limita aos top 15 tipos
    const topData = data.slice(0, 15);
    const categories = topData.map((d) => d.tipo);

    // Calcula valores (absoluto ou percentual)
    const regularValues = topData.map((d) => 
      viewMode === 'percentual' ? calculateImoveisPercent(d.regular, d.total) : d.regular
    );
    const andamentoValues = topData.map((d) => 
      viewMode === 'percentual' ? calculateImoveisPercent(d.andamento, d.total) : d.andamento
    );
    const irregularValues = topData.map((d) => 
      viewMode === 'percentual' ? calculateImoveisPercent(d.irregular, d.total) : d.irregular
    );

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const paramsArray = params as Array<{
            name: string;
            marker: string;
            seriesName: string;
            value: number;
          }>;
          if (!paramsArray.length) return '';
          
          const name = paramsArray[0].name;
          let html = `<strong>${name}</strong><br/>`;
          
          for (const p of paramsArray) {
            const val = viewMode === 'percentual' 
              ? `${p.value.toFixed(1)}%`
              : p.value.toLocaleString('pt-BR');
            html += `${p.marker} ${p.seriesName}: <strong>${val}</strong><br/>`;
          }
          
          return html;
        },
      },
      legend: {
        top: 0,
        textStyle: { color: textColor },
      },
      grid: {
        left: 12,
        right: 12,
        top: 40,
        bottom: 12,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          color: textColor,
          formatter: (value: number) => 
            viewMode === 'percentual' ? `${value}%` : value.toLocaleString('pt-BR'),
        },
        splitLine: {
          lineStyle: { color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
        },
      },
      yAxis: {
        type: 'category',
        data: categories,
        inverse: true,
        axisLabel: {
          color: textColor,
          width: 120,
          overflow: 'truncate',
          ellipsis: '...',
        },
      },
      series: [
        {
          name: 'Regular',
          type: 'bar',
          stack: 'total',
          data: regularValues,
          itemStyle: { color: '#10b981' }, // emerald-500
        },
        {
          name: 'Andamento',
          type: 'bar',
          stack: 'total',
          data: andamentoValues,
          itemStyle: { color: '#f59e0b' }, // amber-500
        },
        {
          name: 'Irregular',
          type: 'bar',
          stack: 'total',
          data: irregularValues,
          itemStyle: { color: '#ef4444' }, // red-500
        },
      ],
    };
  }, [data, viewMode, textColor, isDark]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <div className="animate-pulse text-muted-foreground">Carregando gráfico...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] text-muted-foreground">
        Sem dados disponíveis
      </div>
    );
  }

  // Altura dinâmica baseada no número de barras
  const chartHeight = Math.max(300, Math.min(600, data.slice(0, 15).length * 35 + 60));

  return (
    <div data-pdf-block>
      <ReactECharts
        option={option}
        style={{ height: `${chartHeight}px`, width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
}

// ============================================================================
// SEÇÃO PRINCIPAL
// ============================================================================

interface ImoveisSectionProps {
  className?: string;
}

export function ImoveisSection({ className }: ImoveisSectionProps) {
  const [isExporting, setIsExporting] = useState(false);
  const { data, isLoading } = useImoveisData();
  const { regional, uf, viewMode, setViewMode, imoveisScope, setImoveisScope } = useRelatoriosFilters();

  const kpis = data?.kpis;
  const byType = data?.byType || [];
  const labels = scopeLabels[imoveisScope];

  const handleExportExcel = async () => {
    setIsExporting(true);

    try {
      const queryParams = new URLSearchParams();

      if (regional) {
        queryParams.set('regional', regional);
      }

      if (uf) {
        queryParams.set('uf', uf);
      }

      const scopeResults = await Promise.all(
        (['todos', 'avcb', 'alvara'] as const).map(async (scope) => {
          const finalParams = new URLSearchParams(queryParams.toString());
          finalParams.set('scope', scope);

          const response = await fetch(`${API_BASE_URL}/claro/api/reports/imoveis?${finalParams.toString()}`);

          if (!response.ok) {
            throw new Error(`Falha ao carregar dados do escopo ${scope}`);
          }

          const payload = await response.json() as { result?: ImoveisResponse };
          return payload.result ?? { scope, kpis: undefined, byType: [] };
        })
      );

      const workbook = buildImoveisExportWorkbook({
        todos: scopeResults[0]?.byType || [],
        avcb: scopeResults[1]?.byType || [],
        alvara: scopeResults[2]?.byType || [],
      });

      XLSX.writeFile(workbook, `classificacao-tipo-unidade-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error('Erro ao exportar Excel da classificação por tipo de unidade:', error);
      alert('Erro ao exportar o relatório Excel. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <section id="imoveis" className={cn('scroll-mt-20', className)}>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Imóveis</h2>
          <p className="text-sm text-muted-foreground">
            {labels.subtitle}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1 p-1 rounded-lg bg-muted" aria-label="Escopo de imóveis">
            {IMOVEIS_SCOPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={imoveisScope === option.value}
                onClick={() => setImoveisScope(option.value)}
                className={cn(
                  'px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap',
                  imoveisScope === option.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleExportExcel}
            disabled={isExporting || isLoading}
            className="shrink-0"
          >
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exportando...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Exportar Excel
              </>
            )}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          title="Total Unidades"
          value={kpis?.total || 0}
          icon={<Building2 className="w-5 h-5" />}
          color="default"
          isLoading={isLoading}
        />
        <KpiCard
          title={labels.validosTitle}
          value={kpis?.validos || 0}
          percent={kpis?.validosPercent}
          icon={<CheckCircle className="w-5 h-5" />}
          color="green"
          isLoading={isLoading}
        />
        <KpiCard
          title={labels.mappedTitle}
          value={kpis?.ambos || 0}
          percent={kpis?.ambosPercent}
          icon={<CheckCircle className="w-5 h-5" />}
          color="blue"
          isLoading={isLoading}
        />
        <KpiCard
          title="% Em Andamento"
          value={kpis?.andamento || 0}
          percent={kpis?.andamentoPercent}
          icon={<Clock className="w-5 h-5" />}
          color="amber"
          isLoading={isLoading}
        />
      </div>

      {/* Classificação por Tipo */}
      <div className="bg-card rounded-xl border p-4 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Classificação por Tipo de Unidade</h3>
          
          {/* Toggle Absoluto/Percentual */}
          <div className="flex gap-1 p-1 rounded-lg bg-muted">
            <button
              onClick={() => setViewMode('absoluto')}
              className={cn(
                'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                viewMode === 'absoluto'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Absoluto
            </button>
            <button
              onClick={() => setViewMode('percentual')}
              className={cn(
                'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                viewMode === 'percentual'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Percentual
            </button>
          </div>
        </div>

        {/* Tabela */}
        <div className="mb-6">
          <TiposTable data={byType} viewMode={viewMode} isLoading={isLoading} />
        </div>

        {/* Gráfico Stack */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">
            Distribuição por Tipo (Top 15)
          </h4>
          <StackedChart data={byType} viewMode={viewMode} isLoading={isLoading} />
        </div>

        {/* Legenda/Nota */}
        <div className="mt-4 p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
          <strong>Critérios de Classificação:</strong>
          <ul className="mt-1 space-y-1">
            <li><span className="text-emerald-600">● Regular:</span> {labels.regular}</li>
            <li><span className="text-amber-600">● Em Andamento:</span> {labels.andamento}</li>
            <li><span className="text-red-600">● Irregular:</span> {labels.irregular}</li>
          </ul>
        </div>
      </div>
    </section>
  );
}
